"use client";

import type { AssistantStaticBubbleVariantProps, UserStaticBubbleVariantProps } from "@ui/components/core/BaseChat";
import * as m from "motion/react-m";
import { useCallback, useRef } from "react";
import { AssistantPart, StaticAssistantPart } from "./Assistant/AssistantMessageParts";
import { UserMessagePart } from "./User/UserMessageParts";
import type { JSX } from "react";
import type { ChatMessagePart, ChatPartCompleteProps, ChatScenarioRole } from "./types";

interface MessagePartProps {
  role: ChatScenarioRole;
  part: ChatMessagePart;
  assistantMessageVariants: AssistantStaticBubbleVariantProps;
  animate?: boolean;
  onComplete?: ChatPartCompleteProps["onComplete"];
  userMessageVariants: UserStaticBubbleVariantProps;
}

function MessagePart({
  role,
  part,
  assistantMessageVariants,
  animate = false,
  onComplete,
  userMessageVariants,
}: MessagePartProps): JSX.Element | null {
  if (role === "user") {
    return (
      <UserMessagePart
        part={part}
        assistantMessageVariants={assistantMessageVariants}
        userMessageVariants={userMessageVariants}
      />
    );
  }

  if (animate && onComplete) {
    return <AssistantPart part={part} onComplete={onComplete} assistantMessageVariants={assistantMessageVariants} />;
  }

  return <StaticAssistantPart part={part} assistantMessageVariants={assistantMessageVariants} />;
}

interface MessagePartsListProps {
  messageId?: string;
  role: ChatScenarioRole;
  parts: ChatMessagePart[];
  assistantMessageVariants: AssistantStaticBubbleVariantProps;
  visiblePartCount?: number;
  animateParts?: boolean;
  onPartComplete?: (partIndex: number) => void;
  /** Fires once after each animated part shell finishes its entrance transition. */
  onAnimatedPartShellComplete?: (partIndex: number) => void;
  /**
   * When true, the first part (index 0) enters with a cross-fade instead of a slide-up so it
   * appears already present during a container morph animation.
   */
  morphFirstPart?: boolean;
  userMessageVariants: UserStaticBubbleVariantProps;
}

interface AnimatedMessagePartProps {
  role: ChatScenarioRole;
  part: ChatMessagePart;
  partIndex: number;
  assistantMessageVariants: AssistantStaticBubbleVariantProps;
  onComplete: (partIndex: number) => void;
  onShellEnterComplete?: (partIndex: number) => void;
  /** Suppress the Y-slide on this part's enter (use cross-fade only). */
  morphEnter?: boolean;
  userMessageVariants: UserStaticBubbleVariantProps;
}

function AnimatedMessagePart({
  role,
  part,
  partIndex,
  assistantMessageVariants,
  onComplete,
  onShellEnterComplete,
  morphEnter = false,
  userMessageVariants,
}: AnimatedMessagePartProps): JSX.Element {
  const shellCompleteSentRef = useRef(false);

  const handleComplete = useCallback(() => {
    onComplete(partIndex);
  }, [onComplete, partIndex]);

  const handleShellAnimationComplete = useCallback(() => {
    if (shellCompleteSentRef.current) {
      return;
    }
    shellCompleteSentRef.current = true;
    onShellEnterComplete?.(partIndex);
  }, [onShellEnterComplete, partIndex]);

  return (
    <m.div
      initial={
        morphEnter ? { opacity: 0, transform: "translateY(0rem)" } : { opacity: 0, transform: "translateY(0.375rem)" }
      }
      animate={{ opacity: 1, transform: "translateY(0rem)" }}
      transition={{ type: "spring", stiffness: 400, damping: 30 }}
      onAnimationComplete={handleShellAnimationComplete}
    >
      <MessagePart
        role={role}
        part={part}
        animate
        onComplete={handleComplete}
        assistantMessageVariants={assistantMessageVariants}
        userMessageVariants={userMessageVariants}
      />
    </m.div>
  );
}

export function MessagePartsList({
  messageId,
  role,
  parts,
  assistantMessageVariants,
  visiblePartCount = parts.length,
  animateParts = false,
  onPartComplete,
  onAnimatedPartShellComplete,
  morphFirstPart = false,
  userMessageVariants,
}: MessagePartsListProps): JSX.Element {
  const visibleParts = parts.slice(0, visiblePartCount);
  const shouldAnimateParts = animateParts && onPartComplete !== undefined;

  return (
    <div className='tw-flex tw-flex-col tw-gap-2'>
      {visibleParts.map((part, index) => {
        if (shouldAnimateParts) {
          return (
            <AnimatedMessagePart
              key={`${messageId}-${index}`}
              role={role}
              part={part}
              partIndex={index}
              assistantMessageVariants={assistantMessageVariants}
              onComplete={onPartComplete}
              onShellEnterComplete={onAnimatedPartShellComplete}
              morphEnter={morphFirstPart && index === 0}
              userMessageVariants={userMessageVariants}
            />
          );
        }

        return (
          <div key={`${messageId}-${index}`}>
            <MessagePart
              role={role}
              part={part}
              assistantMessageVariants={assistantMessageVariants}
              userMessageVariants={userMessageVariants}
            />
          </div>
        );
      })}
    </div>
  );
}
